import React, { useEffect, type JSX } from 'react';
import { Helmet } from 'react-helmet';
import { Box, Container, Title, Text, Grid, Card, Timeline, ThemeIcon, List, Button } from '@mantine/core';
import { Link } from 'react-router-dom';
import { IconCheck, IconMessage, IconBrain, IconLock, IconHeart, IconArrowRight } from '@tabler/icons-react';
import AudioIntroduction from '../components/audio/AudioIntroduction';

/**
 * How It Works Page - Explains the AI emotional support experience
 * This page is optimized for SEO with keywords related to AI emotional support
 */
export function HowItWorksPage(): JSX.Element {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      {/* SEO Metadata */}
      <Helmet>
        <title>How AI Emotional Support Works | FeelHeard</title>
        <meta name="description" content="Learn how FeelHeard's AI emotional support works to help you process feelings, gain insights, and improve emotional wellbeing with our guided reflection technology." />
        <meta name="keywords" content="AI emotional support, how emotional support works, digital reflection guide, AI therapy companion, emotional wellbeing technology" />
        <link rel="canonical" href="https://www.feelheard.me/how-it-works" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.feelheard.me/how-it-works" />
        <meta property="og:title" content="How AI Emotional Support Works | FeelHeard" />
        <meta property="og:description" content="Learn how FeelHeard's AI emotional support works to help you process feelings, gain insights, and improve emotional wellbeing." />
        <meta property="og:image" content="https://www.feelheard.me/assets/og-image.jpg" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://www.feelheard.me/how-it-works" />
        <meta property="twitter:title" content="How AI Emotional Support Works | FeelHeard" />
        <meta property="twitter:description" content="Learn how FeelHeard's AI emotional support works to help you process feelings, gain insights, and improve emotional wellbeing." />
        <meta property="twitter:image" content="https://www.feelheard.me/assets/og-image.jpg" />
        
        {/* Schema.org structured data */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "How AI Emotional Support Works | FeelHeard",
            "description": "Learn how FeelHeard's AI emotional support works to help you process feelings, gain insights, and improve emotional wellbeing.",
            "publisher": {
              "@type": "Organization",
              "name": "FeelHeard",
              "logo": {
                "@type": "ImageObject",
                "url": "https://www.feelheard.me/favicon.ico"
              }
            },
            "mainEntity": {
              "@type": "Service",
              "name": "AI Emotional Support",
              "description": "FeelHeard offers AI-powered emotional support services to help users process feelings and improve wellbeing.",
              "provider": {
                "@type": "Organization",
                "name": "FeelHeard"
              },
              "serviceType": "Emotional Support"
            }
          }
        `}</script>
      </Helmet>

      <Box py={60} className="bg-gray-50">
        {/* Hero Section */}
        <Container size="lg">
          <Grid gutter={50}>
            <Grid.Col span={6}>
              <Box className="mb-8">
                <div className="inline-block bg-[#C2A4D6]/20 px-4 py-2 rounded-full mb-4">
                  <span className="text-[#C2A4D6] font-medium">Your Personal Reflection Guide</span>
                </div>
                <Title order={1} className="text-4xl md:text-5xl font-bold text-gray-800 mb-4 font-['Poppins']">
                  How <span className="text-[#C2A4D6]">AI Emotional Support</span> Works for You
                </Title>
                <Text className="text-lg text-gray-600 mb-6 font-['Merriweather']">
                  Discover how FeelHeard's AI technology creates a space for meaningful emotional processing, 
                  guided reflection, and personal growth through compassionate, intelligent conversations.
                </Text>
                <AudioIntroduction />
              </Box>
            </Grid.Col>
            
            <Grid.Col span={6}>
              <Box className="relative bg-white rounded-xl shadow-lg p-6">
                <Timeline active={3} bulletSize={30} lineWidth={2}>
                  <Timeline.Item bullet={<ThemeIcon size={22} radius="xl" color="#C2A4D6"><IconMessage size={14} /></ThemeIcon>}>
                    <Text className="text-xl font-bold mb-1">Express Yourself</Text>
                    <Text className="text-gray-600 mb-4">
                      Share your thoughts, feelings, and experiences through text or voice conversations.
                    </Text>
                  </Timeline.Item>
                  
                  <Timeline.Item bullet={<ThemeIcon size={22} radius="xl" color="#B3D9E6"><IconBrain size={14} /></ThemeIcon>}>
                    <Text className="text-xl font-bold mb-1">Receive Guided Reflection</Text>
                    <Text className="text-gray-600 mb-4">
                      Cora listens and responds with thoughtful questions and insights to help you explore deeper.
                    </Text>
                  </Timeline.Item>
                  
                  <Timeline.Item bullet={<ThemeIcon size={22} radius="xl" color="#F4B6A1"><IconHeart size={14} /></ThemeIcon>}>
                    <Text className="text-xl font-bold mb-1">Gain New Perspectives</Text>
                    <Text className="text-gray-600 mb-4">
                      Discover patterns, insights, and new ways of understanding your emotions and experiences.
                    </Text>
                  </Timeline.Item>
                  
                  <Timeline.Item bullet={<ThemeIcon size={22} radius="xl" color="#A9D0A1"><IconCheck size={14} /></ThemeIcon>}>
                    <Text className="text-xl font-bold mb-1">Build Emotional Resilience</Text>
                    <Text className="text-gray-600">
                      With regular conversations, develop greater emotional awareness and wellbeing over time.
                    </Text>
                  </Timeline.Item>
                </Timeline>
              </Box>
            </Grid.Col>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box py={60}>
        <Container size="lg">
          <Box className="text-center mb-16">
            <Title order={2} className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 font-['Poppins']">
              The FeelHeard <span className="text-[#B3D9E6]">Experience</span>
            </Title>
            <Text className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our AI-powered emotional support platform combines advanced natural language understanding with 
              psychological principles to create a uniquely supportive experience.
            </Text>
          </Box>
          
          <Grid>
            <Grid.Col span={4}>
              <Card shadow="sm" p="xl" radius="md" withBorder className="h-full transition-all hover:shadow-md">
                <ThemeIcon size={50} radius="md" className="mb-4 bg-[#C2A4D6]/20">
                  <IconHeart size={24} className="text-[#C2A4D6]" />
                </ThemeIcon>
                <Title order={3} className="text-xl font-bold mb-3">Empathetic Listening</Title>
                <Text className="text-gray-600">
                  Our AI Reflection Guide attentively follows your conversation, recognizing emotions and validating your experiences 
                  without judgment or interruption.
                </Text>
              </Card>
            </Grid.Col>
            
            <Grid.Col span={4}>
              <Card shadow="sm" p="xl" radius="md" withBorder className="h-full transition-all hover:shadow-md">
                <ThemeIcon size={50} radius="md" className="mb-4 bg-[#B3D9E6]/20">
                  <IconBrain size={24} className="text-[#B3D9E6]" />
                </ThemeIcon>
                <Title order={3} className="text-xl font-bold mb-3">Guided Reflection</Title>
                <Text className="text-gray-600">
                  Through thoughtful questions and gentle prompts, Cora helps you explore your thoughts more deeply, 
                  uncovering insights you might not discover on your own.
                </Text>
              </Card>
            </Grid.Col>
            
            <Grid.Col span={4}>
              <Card shadow="sm" p="xl" radius="md" withBorder className="h-full transition-all hover:shadow-md">
                <ThemeIcon size={50} radius="md" className="mb-4 bg-[#F4B6A1]/20">
                  <IconLock size={24} className="text-[#F4B6A1]" />
                </ThemeIcon>
                <Title order={3} className="text-xl font-bold mb-3">Private & Secure</Title>
                <Text className="text-gray-600">
                  Your conversations are private and protected. We prioritize your data security and maintain 
                  strict confidentiality standards for your peace of mind.
                </Text>
              </Card>
            </Grid.Col>
          </Grid>
        </Container>
      </Box>

      {/* Technology Section */}
      <Box py={60} className="bg-gray-50">
        <Container size="lg">
          <Grid gutter={50} align="center">
            <Grid.Col span={6} order={{ base: 2, sm: 1 }}>
              <Box>
                <div className="inline-block bg-[#B3D9E6]/20 px-4 py-2 rounded-full mb-4">
                  <span className="text-[#B3D9E6] font-medium">Advanced AI Technology</span>
                </div>
                <Title order={2} className="text-3xl font-bold text-gray-800 mb-4 font-['Poppins']">
                  The Science Behind <span className="text-[#B3D9E6]">Emotional Support AI</span>
                </Title>
                <Text className="text-lg text-gray-600 mb-6">
                  FeelHeard combines advanced natural language processing with psychological principles to create
                  meaningful emotional support conversations.
                </Text>
                
                <List spacing="md" size="lg" center icon={
                  <ThemeIcon color="#A9D0A1" size={24} radius="xl">
                    <IconCheck size={16} />
                  </ThemeIcon>
                }>
                  <List.Item>
                    <Text className="text-gray-700 font-medium">Natural Language Understanding</Text>
                    <Text className="text-gray-600">
                      Our AI comprehends nuanced emotions and context in your messages.
                    </Text>
                  </List.Item>
                  
                  <List.Item>
                    <Text className="text-gray-700 font-medium">Emotion Recognition</Text>
                    <Text className="text-gray-600">
                      Advanced patterns recognition identifies emotional states and appropriate responses.
                    </Text>
                  </List.Item>
                  
                  <List.Item>
                    <Text className="text-gray-700 font-medium">Personalized Learning</Text>
                    <Text className="text-gray-600">
                      Your conversations help the AI adapt to your unique communication style.
                    </Text>
                  </List.Item>
                </List>
              </Box>
            </Grid.Col>
            
            <Grid.Col span={6} order={{ base: 1, sm: 2 }}>
              <div className="bg-white p-8 rounded-xl shadow-lg">
                <div className="space-y-4">
                  <div className="flex flex-col space-y-2">
                    <div className="bg-[#B3D9E6]/10 p-4 rounded-lg max-w-[80%] self-end">
                      <Text className="text-gray-700">I've been feeling really anxious about a presentation I have to give next week.</Text>
                    </div>
                    <div className="bg-[#C2A4D6]/10 p-4 rounded-lg max-w-[80%]">
                      <Text className="text-gray-700">That sounds challenging. Presentations can bring up a lot of anxiety. What aspects of the presentation are causing you the most worry?</Text>
                    </div>
                  </div>
                  
                  <div className="flex flex-col space-y-2">
                    <div className="bg-[#B3D9E6]/10 p-4 rounded-lg max-w-[80%] self-end">
                      <Text className="text-gray-700">I think I'm afraid I'll mess up and everyone will judge me. What if I forget what to say?</Text>
                    </div>
                    <div className="bg-[#C2A4D6]/10 p-4 rounded-lg max-w-[80%]">
                      <Text className="text-gray-700">That fear of judgment is really common. Have you had experiences in the past where forgetting something led to negative consequences, or is this more of an anticipatory worry?</Text>
                    </div>
                  </div>
                </div>
              </div>
            </Grid.Col>
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box py={60}>
        <Container size="lg">
          <Box className="text-center mb-16">
            <Title order={2} className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 font-['Poppins']">
              Frequently Asked <span className="text-[#F4B6A1]">Questions</span>
            </Title>
            <Text className="text-lg text-gray-600 max-w-3xl mx-auto">
              Learn more about how our AI emotional support service works and how it can help you.
            </Text>
          </Box>
          
          <Grid>
            <Grid.Col span={6}>
              <Box className="mb-8">
                <Title order={3} className="text-xl font-bold mb-2">Is this the same as therapy?</Title>
                <Text className="text-gray-600">
                  No, FeelHeard is not therapy and does not replace professional mental health services. 
                  It's a supplemental emotional support tool that can help with reflection and processing feelings. 
                  For clinical concerns, please consult a licensed mental health professional.
                </Text>
              </Box>
              
              <Box className="mb-8">
                <Title order={3} className="text-xl font-bold mb-2">How is my data protected?</Title>
                <Text className="text-gray-600">
                  We take your privacy seriously. Your conversations are encrypted, and we maintain strict data 
                  protection policies. We never share your personal information with third parties without your consent.
                  You can read more in our <Link to="/privacy" className="text-[#C2A4D6] hover:underline">Privacy Policy</Link>.
                </Text>
              </Box>
              
              <Box className="mb-8">
                <Title order={3} className="text-xl font-bold mb-2">Who can benefit from AI emotional support?</Title>
                <Text className="text-gray-600">
                  Anyone looking to process emotions, gain personal insights, or have a space for reflection can benefit. 
                  This includes people navigating everyday stressors, seeking personal growth, or wanting a judgment-free 
                  space to express themselves.
                </Text>
              </Box>
            </Grid.Col>
            
            <Grid.Col span={6}>
              <Box className="mb-8">
                <Title order={3} className="text-xl font-bold mb-2">How often should I use FeelHeard?</Title>
                <Text className="text-gray-600">
                  This varies by individual needs. Some people benefit from daily check-ins, while others prefer 
                  weekly reflections or using the service during specific challenging situations. There's no right 
                  or wrong frequency – use it in a way that supports your emotional wellbeing.
                </Text>
              </Box>
              
              <Box className="mb-8">
                <Title order={3} className="text-xl font-bold mb-2">Can I use voice instead of typing?</Title>
                <Text className="text-gray-600">
                  Yes! Our Voice Connect plan allows you to speak directly to Cora. Many users find voice conversations 
                  more natural and expressive than typing, making it easier to articulate complex emotions.
                </Text>
              </Box>
              
              <Box className="mb-8">
                <Title order={3} className="text-xl font-bold mb-2">What if I'm in crisis?</Title>
                <Text className="text-gray-600">
                  FeelHeard is not designed for crisis intervention. If you're experiencing a mental health emergency, 
                  please contact a crisis helpline, call 988 (in the US), or go to your nearest emergency room. We provide 
                  resources for crisis support within the app as well.
                </Text>
              </Box>
            </Grid.Col>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box py={60} className="bg-[#C2A4D6]/10">
        <Container size="md" className="text-center">
          <Title order={2} className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 font-['Poppins']">
            Ready to Experience <span className="text-[#C2A4D6]">FeelHeard</span>?
          </Title>
          <Text className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
            Start your journey to better emotional wellbeing today with our AI-powered reflection guide.
            Try it free for 7 days – no commitment required.
          </Text>
          
          <Button 
            component={Link} 
            to="/register"
            size="lg"
            radius="xl"
            className="bg-[#C2A4D6] hover:bg-[#B390C9] text-white px-8 py-4 transition-all transform hover:scale-105"
            rightSection={<IconArrowRight size={18} />}
          >
            Try Free For 7 Days
          </Button>
          
          <Text className="text-gray-500 mt-4">
            No credit card required for trial. Cancel anytime.
          </Text>
        </Container>
      </Box>
    </>
  );
}

export default HowItWorksPage;